import classNames from "classnames";
import React, { useRef, useEffect, useState } from "react";
import { ArrowRightSLine } from "../IconV2/react";
import { color } from "..";
import TooltipV2 from "../TooltipV2";
import t from "../i18n";
import { css } from "@linaria/core";

const DEFAULT_SUMMARY = t("detail.summary");
const INLINE_CN = "inline";
const MARKER_CN = "marker";
const LINE_CLAMP = 2;

const isTextClamped = (el: HTMLSpanElement) => el.scrollHeight > el.clientHeight;

const Details = ({
  text,
  summary = text,
  lineClamp = LINE_CLAMP,
  className,
  openDelay = 0,
}: {
  text: React.ReactNode;
  summary?: React.ReactNode;
  lineClamp?: number;
  className?: string;
  openDelay?: number;
}) => {
  const summaryTextRef = useRef<HTMLSpanElement>(null);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [clamped, setClamped] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!summaryTextRef.current) {
      return;
    }
    summaryTextRef.current.style.webkitLineClamp = String(lineClamp);
  }, [summaryTextRef, lineClamp]);

  useEffect(() => {
    if (!summaryTextRef.current || !detailsRef.current) {
      return;
    }
    detailsRef.current.style.webkitLineClamp = String(lineClamp);
    const textElement = summaryTextRef.current;
    if (!textElement) return;
    const resizeObserver = new ResizeObserver(entries => {
      for (const _ of entries) {
        const clamped = isTextClamped(textElement);
        setClamped(clamped);
      }
    });

    const clamped = isTextClamped(textElement);
    if (clamped) setClamped(true);
    resizeObserver.observe(detailsRef.current);

    return () => resizeObserver.disconnect();
  }, [detailsRef, summaryTextRef, lineClamp]);

  if (!text && !summary) return null;

  return (
    <TooltipV2
      placement="bottom"
      overlay={t("details.open.hint")}
      style={{ visibility: clamped && open ? "visible" : "hidden" }}
      arrowWidth={7}
      openDelay={openDelay}
    >
      <details
        ref={detailsRef}
        className={classNames(details, className, {
          [INLINE_CN]: summary === text,
          [MARKER_CN]: !(summary === text) || clamped,
        })}
        onClick={e => {
          if (clamped) e.stopPropagation();
          setOpen(e.currentTarget.open);
        }}
      >
        <summary>
          <ArrowRightSLine />
          <span ref={summaryTextRef}>{summary || DEFAULT_SUMMARY}</span>
        </summary>
        <div>{text}</div>
      </details>
    </TooltipV2>
  );
};

export default Details;

const details = css`
  overflow: hidden;

  & {
    --chevron-size: 1.5em;
  }

  svg {
    height: var(--chevron-size);
    width: var(--chevron-size);
  }

  &,
  summary {
    position: relative;
  }

  summary {
    list-style: none;
  }

  &.${INLINE_CN}[open] summary,
  summary svg {
    position: absolute;
    top: 0;
    left: 0;
    color: ${color.titanium500};
    transition: transform 150ms ease-in-out;
  }

  summary span {
    display: inline-block;
  }

  &.${MARKER_CN} {
    cursor: pointer;
  }

  &.${INLINE_CN} summary > span {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &.${MARKER_CN} summary svg {
    display: inline-block;
    top: 0;
    left: 0;
  }

  &.${MARKER_CN} summary > span,
  &.${INLINE_CN}.${MARKER_CN}[open] > div {
    padding-left: var(--chevron-size);
  }

  &.${INLINE_CN}[open] summary {
    bottom: 0;
    right: 0;
  }

  & summary svg,
  &.${MARKER_CN}[open] summary svg {
    display: none;
  }

  &.${MARKER_CN}[open] summary svg {
    display: inline-block;
    transform: rotate(90deg);
  }

  &.${INLINE_CN}[open] summary span {
    opacity: 0;
    pointer-events: none;
  }
`;
